import { useQueryClient } from '@tanstack/react-query';

import { gql } from '@/utilities/gql';
import { makeGqlMutation } from '@/utilities/gqlQuery';
import { useSnackbar } from '@/features/snackbar';

import type { PortfolioQueryClientData } from '@/hooks/portfolio/usePortfolio';
import {
  PORTFOLIO_QUERY_KEY,
  PORTFOLIO_FRAGMENT,
  PORTFOLIOS_QUERY_KEY,
} from '@/hooks/portfolio/usePortfolio';
import { AGGREGATE_PORTFOLIO_QUERY_KEY } from '@/hooks/portfolio/useAggregatePortfolio';
import { PORTFOLIOS_PERFORMANCE_TIMESERIES_QUERY_KEY } from '@/hooks/portfolio/usePortfolioPerformanceTimeseries';

import type { ResultOf, VariablesOf } from '@/utilities/gql';

const MUTATION = gql(
  `
  mutation updateTransactionPortfolio($id: String!, $transactions: [ManualTransactionInput!]!) {
    updateTransactionPortfolio(id: $id, transactions: $transactions) {
      ...portfolioFields
    }
  }
`,
  [PORTFOLIO_FRAGMENT]
);

export type TransactionInput = VariablesOf<
  typeof MUTATION
>['transactions'][number];

const mutation = makeGqlMutation(MUTATION);

interface Props {
  onSuccess?: (data: ResultOf<typeof MUTATION>) => void;
}

export const useUpdateTransactionPortfolio = ({ onSuccess }: Props = {}) => {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();

  return mutation({
    onSuccess: (data, { id: portfolioId }) => {
      addSnackbar({
        type: 'positive',
        message: `All changes saved successfully!`,
        lifeTime: 3000,
      });
      const portfolio = data.updateTransactionPortfolio;
      queryClient.setQueryData<PortfolioQueryClientData>(
        [PORTFOLIO_QUERY_KEY, portfolioId],
        () => portfolio
      );
      onSuccess?.(data);
    },
    onSettled: (_, __, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [PORTFOLIO_QUERY_KEY, id],
      });
      queryClient.invalidateQueries({ queryKey: PORTFOLIOS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: AGGREGATE_PORTFOLIO_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: [PORTFOLIOS_PERFORMANCE_TIMESERIES_QUERY_KEY, id],
      });
    },
  });
};
